import { useState, useEffect } from "react";
import { Text, Link, Popover, Button, Spinner } from "@radix-ui/themes";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import { rewriteUrl } from "~/utils/rewrite-news-urls";
import axios from "axios";

interface NewsPayload {
  transmission_id: string;
  uuid: string;
  datetime: string;
  headline: string;
  summary: string;
  symbols: string[];
  metacodes: string[];
}

const NewsPopover = ({
  url,
  provider,
  children,
  collisionPadding,
  avoidCollisions = false,
  maxWidth = "450px",
  truncateWords,
  linkText = "Pebble News Viewer",
}: {
  url: string;
  provider: string;
  children: React.ReactNode;
  collisionPadding?: number;
  avoidCollisions?: boolean;
  maxWidth?: string;
  truncateWords?: number;
  linkText?: string;
}) => {
  const [newsData, setNewsData] = useState<NewsPayload | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // lesser of two evils: useEffect with resize listener vs duplicating entire popover components
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  const fetchNewsData = async () => {
    if (newsData) return;
    setIsLoading(true);
    setError(null);
    try {
      // Append ?format=json to the URL
      const apiUrl = `${rewriteUrl(url, true, provider)}${
        url.includes("?") ? "&" : "?"
      }format=json`;
      const response = await axios.get(apiUrl);

      if (!response.data) {
        throw new Error(`Error fetching news: ${response.statusText}`);
      }
      setNewsData(response.data);
    } catch (err) {
      console.error("Error fetching news data:", err);
      setError(err instanceof Error ? err.message : "Failed to load news");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Popover.Root
      onOpenChange={(open) => {
        if (open) fetchNewsData();
      }}
    >
      <Popover.Trigger>
        <span>{children}</span>
      </Popover.Trigger>

      <Popover.Content
        side="bottom"
        align={isMobile ? "center" : "start"}
        sideOffset={8}
        alignOffset={0}
        collisionPadding={collisionPadding}
        avoidCollisions={avoidCollisions}
        className={isMobile ? "w-[calc(100vw-32px)] max-w-none" : ""}
        style={isMobile ? {} : { maxWidth }}
      >
        {isLoading && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "1rem",
            }}
          >
            <Spinner size="2" />
          </div>
        )}
        {error && (
          <Text as="span" color="red" size="2">
            {error}
          </Text>
        )}
        {!isLoading && newsData && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.5rem",
            }}
          >
            <Text size="3" weight="bold">
              {newsData.headline}
            </Text>
            <Text size="1" color="gray">
              {new Date(newsData.datetime).toLocaleString()}
            </Text>
            {(() => {
              const hasHtml =
                newsData.summary.includes("<html>") ||
                newsData.summary.includes("<pre>");
              const hasP = newsData.summary.includes("<p>");

              if (hasHtml && !hasP) return null;

              // Decode HTML entities for proper rendering
              const decodeHtmlEntities = (text: string) => {
                const textarea = document.createElement("textarea");
                textarea.innerHTML = text;
                return textarea.value;
              };

              // Truncate to word limit if specified
              const truncateText = (text: string, wordLimit?: number) => {
                if (!wordLimit) return text;

                const words = text.split(/\s+/);
                if (words.length <= wordLimit) return text;

                return words.slice(0, wordLimit).join(" ") + "...";
              };

              const processedSummary = truncateWords
                ? truncateText(newsData.summary, truncateWords)
                : newsData.summary;

              return hasHtml || hasP ? (
                <Text
                  size="1"
                  className="max-h-96 overflow-y-scroll [&_p]:my-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:ml-4"
                  asChild
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: decodeHtmlEntities(processedSummary),
                    }}
                  />
                </Text>
              ) : (
                <Text size="1" className="max-h-96 overflow-y-scroll">
                  {processedSummary}
                </Text>
              );
            })()}
            <div className="pt-1 ml-auto">
              <Button size="1" variant="soft" asChild>
                <Link
                  href={rewriteUrl(url, false, provider)}
                  className="no-underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {linkText} <ExternalLinkIcon />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </Popover.Content>
    </Popover.Root>
  );
};

export default NewsPopover;
